// Concern-code catalog for the Catalog Manager.
//
// Mirrored from
// `b2b-infrastructure/api/common/food_ingest/concern_flagging.py` (class
// `ConcernCodes`). Concern codes are auto-applied by the ingest pipeline's
// hardcoded sanity checks; this table lets the UI show their names/severity
// (instead of raw numbers) and decide what hard-blocks ingestion.
//
// TODO(concern-catalog): share a single source of truth with the Python
// `ConcernCodes` (e.g. generate this from advisor metadata) so the two can't
// drift. For now keep codes/severity in sync by hand.
import { PassioFood } from "./types";

export interface ConcernDef {
  code: number;
  name: string;
  description: string;
  /** 1 = blocking (hard), 2 = warning, 3 = info. */
  severity: number;
}

/**
 * The authoritative set of concern codes that hard-block ingestion. This is a
 * 1:1 mirror of `UPDATE_BLOCK_CONCERNS` in
 * `b2b-infrastructure/api/common/food_ingest/decision.py` (itself copied from
 * `platform/scripts/groundingsearch/update_decision.py`). The backend blocks
 * create/update on exactly these codes, so the UI must use the same set rather
 * than guessing from `severity` — plenty of items carry non-blocking concerns.
 *
 * Keep in sync with the Python set by hand.
 */
export const BLOCKING_CONCERN_CODES: ReadonlySet<number> = new Set([
  1000, 1001, 1002, 1003, 1004, 1005, 1006, 1007, 1100, 1102, 1103, 1104, 1105,
  1107, 1108, 1200, 1201, 1203, 1204, 1205, 1206, 1207, 1208, 1209, 1210, 1211,
  1212, 1213, 1215, 1302,
]);

/**
 * Which editable area a blocking concern points at, so the editor can highlight
 * the fields the user needs to fix. Codes not listed default to "nutrients".
 */
export type ConcernFieldTarget =
  | "identity"
  | "nutrients"
  | "portions"
  | "brand"
  | "ingredients";

export const CONCERN_FIELD_TARGET: Record<number, ConcernFieldTarget> = {
  // Nutrient relationships / quality
  1000: "nutrients",
  1001: "nutrients",
  1002: "nutrients",
  1003: "nutrients",
  1004: "nutrients",
  1005: "ingredients",
  1006: "nutrients",
  1007: "nutrients",
  1200: "nutrients",
  1201: "nutrients",
  1203: "nutrients",
  1206: "nutrients",
  1207: "nutrients",
  1208: "nutrients",
  1209: "nutrients",
  1210: "nutrients",
  1211: "nutrients",
  1212: "nutrients",
  1213: "nutrients",
  1215: "nutrients",
  // Portions / serving
  1100: "portions",
  1102: "portions",
  1103: "portions",
  1104: "portions",
  1105: "portions",
  1107: "portions",
  1108: "portions",
  1205: "portions",
  // Identity / brand
  1204: "identity",
  1302: "brand",
};

export const CONCERN_CATALOG: Record<number, ConcernDef> = {
  // Nutrient relationship concerns (1000-1099)
  1000: {
    code: 1000,
    name: "NET_CARBS_AMOUNT_ISSUE",
    description: "Net carbs calculation doesn't match ingredient composition",
    severity: 1,
  },
  1001: {
    code: 1001,
    name: "SUSPECT_CALORIES",
    description:
      "Calorie count appears inconsistent with macro nutrient values",
    severity: 1,
  },
  1002: {
    code: 1002,
    name: "MISSING_ESSENTIAL_NUTRIENTS",
    description:
      "Missing critical nutrients such as calories or macronutrients",
    severity: 1,
  },
  1003: {
    code: 1003,
    name: "FAT_AMOUNT_ISSUE",
    description: "Fat subcomponents don't sum correctly",
    severity: 1,
  },
  1004: {
    code: 1004,
    name: "SUGAR_AMOUNT_ISSUE",
    description: "Added sugars exceed total sugars",
    severity: 1,
  },
  1005: {
    code: 1005,
    name: "SUGAR_ALCOHOL_MISMATCH",
    description:
      "Ingredients mention sugar alcohol but no Sugar Alcohol nutrient present",
    severity: 3,
  },
  1006: {
    code: 1006,
    name: "MISSING_ESSENTIAL_MICROS",
    description:
      "Missing essential micros usually provided on nutrition fact sheets",
    severity: 2,
  },
  1007: {
    code: 1007,
    name: "CARBS_AMOUNT_ISSUE",
    description: "Carbs amount is inconsistent with ingredient composition",
    severity: 1,
  },
  1008: {
    code: 1008,
    name: "MISSING_MICROS",
    description:
      "Missing essential micros usually provided on nutrition fact sheets",
    severity: 2,
  },
  1009: {
    code: 1009,
    name: "FIBER_MISMATCH",
    description: "Ingredients mention fiber but no Fiber nutrient present",
    severity: 2,
  },

  // Portion/serving concerns (1100-1199)
  1100: {
    code: 1100,
    name: "INVALID_SERVING_SIZE",
    description:
      "No serving size present or existing one has invalid weight value",
    severity: 1,
  },
  1102: {
    code: 1102,
    name: "INCONSISTENT_PORTIONS",
    description:
      "Portion measurements don't align with standard conversion ratios",
    severity: 3,
  },
  1103: {
    code: 1103,
    name: "INVALID_PORTION_NAME",
    description: "Portion name is missing or invalid",
    severity: 1,
  },
  1104: {
    code: 1104,
    name: "PORTION_WEIGHT_TOO_HIGH",
    description: "Portion weight exceeds reasonable limits",
    severity: 1,
  },
  1105: {
    code: 1105,
    name: "INVALID_OUNCE_WEIGHT",
    description: "Ounce portion weight outside valid range",
    severity: 1,
  },
  1106: {
    code: 1106,
    name: "GRAMS_IN_PORTION_NAME",
    description: "Portion name contains unit that should be in weight field",
    severity: 3,
  },
  1107: {
    code: 1107,
    name: "DUPLICATE_PORTION_NAME",
    description: "Multiple portions have the same name when case is ignored",
    severity: 3,
  },
  1108: {
    code: 1108,
    name: "BAD_SERVING_CONFIG",
    description: "Portion with 'serving' in name has default weight of 1",
    severity: 3,
  },
  1109: {
    code: 1109,
    name: "ONLY_GENERIC_PORTIONS",
    description:
      "Item has no portions except generic units like oz, fl oz, gram, or millilitre",
    severity: 2,
  },

  // Data quality concerns (1200-1299)
  1200: {
    code: 1200,
    name: "INVALID_NUTRIENT_RANGES",
    description: "One or more nutrient values fall outside reasonable ranges",
    severity: 1,
  },
  1201: {
    code: 1201,
    name: "SUSPECT_UNIT_VALUES",
    description:
      "Nutrient units appear to be incorrectly scaled or use wrong magnitude",
    severity: 1,
  },
  1203: {
    code: 1203,
    name: "DUPLICATE_NUTRIENTS",
    description: "Multiple entries for the same nutrient",
    severity: 2,
  },
  1204: {
    code: 1204,
    name: "MISSING_FOOD_NAME",
    description: "Food name is missing",
    severity: 1,
  },
  1205: {
    code: 1205,
    name: "INVALID_WEIGHT_UNIT",
    description: "Weight unit is not g or ml",
    severity: 1,
  },
  1206: {
    code: 1206,
    name: "MAX_SODIUM_EXCEEDED",
    description: "Maximum sodium exceeded",
    severity: 1,
  },
  1207: {
    code: 1207,
    name: "MAX_SALT_EXCEEDED",
    description: "Maximum salt exceeded",
    severity: 2,
  },
  1208: {
    code: 1208,
    name: "MAX_FIBRE_EXCEEDED",
    description: "Maximum fibre exceeded",
    severity: 2,
  },
  1209: {
    code: 1209,
    name: "MAX_MINERALS_EXCEEDED",
    description: "A value for minerals exceeded",
    severity: 2,
  },
  1210: {
    code: 1210,
    name: "MAX_ENERGY_EXCEEDED",
    description: "Maximum energy exceeded",
    severity: 2,
  },
  1211: {
    code: 1211,
    name: "SUM_NUTRIENTS_EXCEEDED",
    description: "The sum of all relevant nutrients exceeded",
    severity: 1,
  },
  1212: {
    code: 1212,
    name: "SODIUM_SALT_MISMATCH",
    description: "Sodium and salt values don't match",
    severity: 3,
  },
  1213: {
    code: 1213,
    name: "ENERGY_CALORIES_MISMATCH",
    description: "Energy and calories values don't match",
    severity: 2,
  },
  1214: {
    code: 1214,
    name: "POTENTIAL_SODIUM_ISSUE",
    description: "Potential sodium issue",
    severity: 2,
  },
  1215: {
    code: 1215,
    name: "NEGATIVE_NUTRIENT_AMOUNT",
    description: "A nutrient amount is negative",
    severity: 1,
  },

  // Branded item concerns (1300-1399)
  1300: {
    code: 1300,
    name: "MISSING_INGREDIENTS",
    description: "Branded item is missing ingredient list",
    severity: 3,
  },
  1301: {
    code: 1301,
    name: "INVALID_INGREDIENT_FORMAT",
    description: "Ingredient list is not properly formatted",
    severity: 3,
  },
  1302: {
    code: 1302,
    name: "MISSING_BRAND_INFO",
    description:
      "Product has a code but missing brand name or owner information",
    severity: 3,
  },
  1303: {
    code: 1303,
    name: "MISSING_PRODUCT_CODE",
    description: "Brand name or owner exists but missing product code",
    severity: 3,
  },

  // Tag consistency concerns (1400-1499)
  1400: {
    code: 1400,
    name: "VEGAN_TAG_INCONSISTENCY",
    description: "Vegan tag conflicts with dairy/egg content",
    severity: 3,
  },
  1401: {
    code: 1401,
    name: "ALCOHOL_TAG_INCONSISTENCY",
    description: "Alcohol tag/nutrient mismatch",
    severity: 3,
  },
  1402: {
    code: 1402,
    name: "CAFFEINE_TAG_INCONSISTENCY",
    description: "Caffeine tag/nutrient mismatch",
    severity: 3,
  },
};

export interface ConcernView {
  code: number;
  name: string;
  description: string;
  severity: number;
  blocking: boolean;
}

/** Resolve a concern code to a display object, preferring server-provided detail. */
export const describeConcern = (
  code: number,
  detail?: { name?: string; description?: string; severity?: number }
): ConcernView => {
  const def = CONCERN_CATALOG[code];
  const severity = detail?.severity ?? def?.severity ?? 2;
  return {
    code,
    name: detail?.name || def?.name || `CONCERN_${code}`,
    description: detail?.description || def?.description || "",
    severity,
    // Blocking is decided by the authoritative code set, NOT severity — many
    // severity-1-looking concerns don't block, and a few higher ones do.
    blocking: BLOCKING_CONCERN_CODES.has(code),
  };
};

// Sugar-alcohol ingredient keywords (subset mirror of the backend list) so the
// UI can name the specific ingredient that tripped SUGAR_ALCOHOL_MISMATCH.
const SUGAR_ALCOHOL_KEYWORDS = [
  "erythritol",
  "xylitol",
  "sorbitol",
  "maltitol",
  "mannitol",
  "lactitol",
  "isomalt",
  "glycerin",
  "glycerol",
];

const shortNameSet = (food?: PassioFood | null): Set<string> => {
  const s = new Set<string>();
  (food?.nutrients || []).forEach((n) => {
    const sn = (n?.nutrient?.shortName || n?.nutrient?.name || "")
      .toString()
      .toLowerCase();
    if (sn) s.add(sn);
  });
  return s;
};

const amountByShortName = (
  food: PassioFood | null | undefined,
  shortName: string
): number => {
  const hit = (food?.nutrients || []).find(
    (n) =>
      (n?.nutrient?.shortName || n?.nutrient?.name || "")
        .toString()
        .toLowerCase() === shortName
  );
  return typeof hit?.amount === "number" ? hit.amount : 0;
};

/**
 * A specific, actionable explanation for a concern given the ACTUAL food data
 * (e.g. exactly which micro is missing), falling back to the generic catalog
 * text. This is what makes "Can't write yet" self-explanatory to a non-expert.
 */
export const explainConcern = (
  code: number,
  food?: PassioFood | null
): string => {
  const generic = CONCERN_CATALOG[code]?.description || "";
  if (!food) return generic;
  const have = shortNameSet(food);
  // Helpers for data-specific numbers (values are per 100g).
  const a = (short: string) => amountByShortName(food, short);
  const r = (n: number) => Math.round(n * 10) / 10;

  switch (code) {
    case 1002: {
      // MISSING_ESSENTIAL_NUTRIENTS — name the missing macro(s).
      const missing: string[] = [];
      if (!have.has("calories")) missing.push("Calories");
      if (!have.has("carbs")) missing.push("Carbs");
      if (!have.has("protein")) missing.push("Protein");
      if (!have.has("fat")) missing.push("Fat");
      return missing.length
        ? `Missing macro(s): ${missing.join(
            ", "
          )}. Add them (per serving), then Re-check.`
        : generic;
    }
    case 1006: {
      // MISSING_ESSENTIAL_MICROS — most often it's Sodium; also flags carbs
      // without fiber/sugar. Name what's actually absent.
      const missing: string[] = [];
      if (!have.has("sodium")) missing.push("Sodium");
      const carbs = amountByShortName(food, "carbs");
      if (carbs > 0.5) {
        if (!have.has("fiber")) missing.push("Dietary Fiber");
        if (!have.has("sugartotal")) missing.push("Total Sugars");
      }
      return missing.length
        ? `The label almost certainly lists these — add them, then Re-check: ${missing.join(
            ", "
          )}.`
        : generic;
    }
    case 1008: {
      // MISSING_MICROS — fat present but sat-fat / cholesterol absent.
      const missing: string[] = [];
      if (!have.has("satfat")) missing.push("Saturated Fat");
      if (!have.has("cholesterol")) missing.push("Cholesterol");
      return missing.length
        ? `Fat is present but these aren't — add from the label: ${missing.join(
            ", "
          )}.`
        : generic;
    }
    case 1005: {
      // SUGAR_ALCOHOL_MISMATCH — name the ingredient that triggered it.
      const ing = (food.branded?.ingredients || "").toString().toLowerCase();
      const name = (food.name || "").toString().toLowerCase();
      const hit = SUGAR_ALCOHOL_KEYWORDS.find(
        (k) => ing.includes(k) || name.includes(k)
      );
      return hit
        ? `Ingredients list “${hit}”, a sugar alcohol, but there's no Sugar Alcohol nutrient value. Add it (grams per serving), then Re-check.`
        : generic;
    }
    case 1001: {
      // SUSPECT_CALORIES — compare stated calories to the macro estimate.
      const cal = a("calories");
      const fat = a("fat");
      const carbs = a("carbs");
      const protein = a("protein");
      const fiber = a("fiber");
      const est = fat * 9 + Math.max(0, carbs - fiber) * 4 + protein * 4;
      return `Stated ${r(cal)} kcal doesn't match the macros (fat ${r(
        fat
      )}g, carbs ${r(carbs)}g, protein ${r(protein)}g ≈ ${r(
        est
      )} kcal per 100g). Fix the calories or the macro that's off, then Re-check.`;
    }
    case 1003: {
      // FAT_AMOUNT_ISSUE — sub-fats exceed total fat.
      const fat = a("fat");
      const sub =
        a("satfat") +
        a("transfat") +
        a("monounsaturatedfat") +
        a("polyunsaturatedfat");
      return `Saturated + trans + mono + poly fat (${r(
        sub
      )}g) exceed Total Fat (${r(
        fat
      )}g) per 100g. Correct the fat breakdown, then Re-check.`;
    }
    case 1007: {
      // CARBS_AMOUNT_ISSUE — carb components exceed total carbs.
      const carbs = a("carbs");
      const sub = a("fiber") + a("sugartotal") + a("sugaralcohol");
      return `Fiber + sugars + sugar alcohol (${r(
        sub
      )}g) exceed Total Carbohydrate (${r(
        carbs
      )}g) per 100g. Correct the carb breakdown, then Re-check.`;
    }
    case 1004: {
      // SUGAR_AMOUNT_ISSUE — added sugars > total sugars.
      const total = a("sugartotal");
      const added = a("sugaradded");
      return `Added Sugars (${r(added)}g) exceed Total Sugars (${r(
        total
      )}g) per 100g — added can't be more than total. Fix one, then Re-check.`;
    }
    case 1009:
      // FIBER_MISMATCH
      return "Ingredients mention fiber but there's no Dietary Fiber value. Add Fiber (g per serving), then Re-check.";
    case 1212: {
      // SODIUM_SALT_MISMATCH
      const sodium = a("sodium");
      const salt = a("salt");
      return `Sodium (${r(sodium)}mg) and Salt (${r(
        salt
      )}mg) don't line up — salt should be about 2.5× sodium. We auto-fill a missing one; if you entered both, correct one of them, then Re-check.`;
    }
    case 1206:
      return `Sodium is ${r(
        a("sodium")
      )}mg per 100g, which is implausibly high — it's likely mis-scaled (mg vs g) or a typo. Check the value/unit, then Re-check.`;
    case 1211:
      return "The macro + mineral values sum to more than 100 g per 100 g of food — a value is almost certainly mis-scaled (e.g. mg entered as g). Check the nutrient units, then Re-check.";
    case 1201:
      return "A nutrient's unit looks wrong: fat/carbs/protein must be g, sodium/minerals mg, calories kcal. Fix the offending unit, then Re-check.";
    case 1215: {
      // NEGATIVE_NUTRIENT_AMOUNT — name the offender.
      const neg = (food.nutrients || []).find(
        (n) => typeof n?.amount === "number" && (n.amount as number) < 0
      );
      const nm =
        neg?.nutrient?.name || neg?.nutrient?.shortName || "A nutrient";
      return `${nm} has a negative amount, which isn't possible. Set it to 0 or the correct value, then Re-check.`;
    }
    case 1100:
      return "No valid serving/portion. Add at least one portion with a positive gram or ml weight, then Re-check.";
    case 1103:
      return "A portion is missing its name. Give every portion a name (e.g. “bar”, “cup”), then Re-check.";
    case 1104:
      return "A portion weighs over 700 g, which is out of range. Check the portion weight, then Re-check.";
    case 1105:
      return "The “oz” portion isn't ~28.3 g. Correct or remove it, then Re-check.";
    case 1205:
      return "A portion uses a unit other than g or ml. Portion weights must be in g or ml — fix it, then Re-check.";
    case 1204:
      return "The product has no name. Add a product name, then Re-check.";
    case 1302:
      return "This has a UPC/product code but no brand name or owner. Add the brand in the editor (Product info), then Re-check.";
    case 1303:
      // MISSING_PRODUCT_CODE
      return "No UPC/barcode was captured. Add the product code in the editor (Product info) — it isn't strictly required, but strongly recommended.";
    default:
      return generic;
  }
};

/** Whether any of the given codes hard-block ingestion. */
export const hasBlockingConcern = (codes?: number[] | null): boolean =>
  Array.isArray(codes) && codes.some((c) => BLOCKING_CONCERN_CODES.has(c));

/** Just the blocking codes from a list. */
export const blockingConcerns = (codes?: number[] | null): number[] =>
  Array.isArray(codes)
    ? codes.filter((c) => BLOCKING_CONCERN_CODES.has(c))
    : [];

/** The set of editor field-areas the blocking concerns point at. */
export const blockingFieldTargets = (
  codes?: number[] | null
): Set<ConcernFieldTarget> => {
  const out = new Set<ConcernFieldTarget>();
  blockingConcerns(codes).forEach((c) => {
    out.add(CONCERN_FIELD_TARGET[c] ?? "nutrients");
  });
  return out;
};
